import {
  IconDefinition,
  faCheckSquare,
  faCog,
  faEye,
  faFileExport,
  faInfoCircle,
  faPlusSquare,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState } from 'react';
import { UseFormRegisterReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Checkbox } from 'src/common/components/inputs/Checkbox';
import { InlineMessage } from 'src/common/components/InlineMessage';
import { Modal } from 'src/common/components/Modal';
import { Button } from 'src/common/components/buttons/Button';
import { useProject } from 'src/project/project-context';
import { LicenseType } from 'src/project/types/projects';
import { TechCrewPermission } from 'src/project/types/techCrews';

type Props = {
  permissions: TechCrewPermission[];
  isOpen: boolean;
  isAdmin: boolean;
  onClose: () => void;
  onSubmit: (newPermissions: TechCrewPermission[]) => Promise<void>;
};

type FormState = {
  viewDocumentation: boolean;
  editDocumentation: boolean;
  controlDocumentation: boolean;
  viewAttendanceNamesWorktimeHours: boolean;
  viewAttendanceNamesCompanyPeopleComment: boolean;
  viewBoqPositionsHours: boolean;
  viewAttendancePeopleCount: boolean;
  viewAttendanceTotalHours: boolean;
  exportDataDocumentation: boolean;
  viewDocuments: boolean;
  viewAnalysis: boolean;
  editAnalysis: boolean;
};

export const EditPermissionsModal = ({
  permissions,
  isOpen,
  isAdmin,
  onClose,
  onSubmit,
}: Props) => {
  const { t } = useTranslation(['common', 'project']);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
  } = useForm<FormState>({
    defaultValues: {
      viewDocumentation: permissions.includes(
        TechCrewPermission.VIEW_DOCUMENTATION,
      ),
      editDocumentation: permissions.includes(
        TechCrewPermission.EDIT_DOCUMENTATION,
      ),
      controlDocumentation: permissions.includes(
        TechCrewPermission.CONTROL_DOCUMENTATION,
      ),
      viewAttendanceNamesWorktimeHours: permissions.includes(
        TechCrewPermission.VIEW_ATTENDANCE_NAMES_WORKTIME_HOURS,
      ),
      viewAttendanceNamesCompanyPeopleComment: permissions.includes(
        TechCrewPermission.VIEW_ATTENDANCE_NAMES_COMPANY_PEOPLE_COMMENT,
      ),
      viewBoqPositionsHours: permissions.includes(
        TechCrewPermission.VIEW_BOQ_POSITIONS_HOURS,
      ),
      viewAttendancePeopleCount: permissions.includes(
        TechCrewPermission.VIEW_ATTENDANCE_PEOPLE_COUNT,
      ),
      viewAttendanceTotalHours: permissions.includes(
        TechCrewPermission.VIEW_ATTENDANCE_TOTAL_HOURS,
      ),
      exportDataDocumentation: permissions.includes(
        TechCrewPermission.EXPORT_DATA_DOCUMENTATION,
      ),
      viewDocuments: permissions.includes(TechCrewPermission.VIEW_DOCUMENTS),
      viewAnalysis: permissions.includes(TechCrewPermission.VIEW_ANALYSIS),
      editAnalysis: permissions.includes(TechCrewPermission.EDIT_ANALYSIS),
    },
  });

  // header checkbox: checked if all documentation permissions are checked
  const isDocumentationChecked =
    watch('viewDocumentation') &&
    watch('editDocumentation') &&
    watch('controlDocumentation') &&
    watch('viewAttendanceNamesWorktimeHours') &&
    watch('viewAttendanceNamesCompanyPeopleComment') &&
    watch('viewBoqPositionsHours') &&
    watch('viewAttendancePeopleCount') &&
    watch('viewAttendanceTotalHours') &&
    watch('exportDataDocumentation') &&
    watch('viewDocuments');
  // header checkbox: indeterminate if some documentation permissions are checked
  const isDocumentationIndeterminate = watch('viewDocumentation')
    ? !watch('editDocumentation') ||
      !watch('controlDocumentation') ||
      !watch('viewAttendanceNamesWorktimeHours') ||
      !watch('viewAttendanceNamesCompanyPeopleComment') ||
      !watch('viewBoqPositionsHours') ||
      !watch('viewAttendancePeopleCount') ||
      !watch('viewAttendanceTotalHours') ||
      !watch('exportDataDocumentation') ||
      !watch('viewDocuments')
    : watch('editDocumentation') ||
      watch('controlDocumentation') ||
      watch('viewAttendanceNamesWorktimeHours') ||
      watch('viewAttendanceNamesCompanyPeopleComment') ||
      watch('viewBoqPositionsHours') ||
      watch('viewAttendancePeopleCount') ||
      watch('viewAttendanceTotalHours') ||
      watch('exportDataDocumentation') ||
      watch('viewDocuments');
  // view checkbox: enforced if edit is checked
  const needsViewDocumentation =
    watch('editDocumentation') ||
    watch('controlDocumentation') ||
    watch('viewAttendanceNamesWorktimeHours') ||
    watch('viewAttendanceNamesCompanyPeopleComment') ||
    watch('viewBoqPositionsHours') ||
    watch('viewAttendancePeopleCount') ||
    watch('viewAttendanceTotalHours') ||
    watch('exportDataDocumentation');

  // header checkbox: checked if all analysis permissions are checked
  const isAnalysisChecked = watch('viewAnalysis') && watch('editAnalysis');
  // header checkbox: indeterminate if some analysis permissions are checked
  const isAnalysisIndeterminate = watch('viewAnalysis')
    ? !watch('editAnalysis')
    : watch('editAnalysis');
  // view checkbox: enforced if edit is checked
  const needsViewAnalysis = watch('editAnalysis');

  // BoQ positions requires at least one attendance name-level permission
  const canEnableBoq =
    watch('viewAttendanceNamesWorktimeHours') ||
    watch('viewAttendanceNamesCompanyPeopleComment');

  function onSave(state: FormState) {
    const newPermissions = [
      ...(needsViewDocumentation || state.viewDocumentation
        ? [TechCrewPermission.VIEW_DOCUMENTATION]
        : []),
      ...(state.editDocumentation
        ? [TechCrewPermission.EDIT_DOCUMENTATION]
        : []),
      ...(state.controlDocumentation
        ? [TechCrewPermission.CONTROL_DOCUMENTATION]
        : []),
      ...(state.viewAttendanceNamesWorktimeHours
        ? [TechCrewPermission.VIEW_ATTENDANCE_NAMES_WORKTIME_HOURS]
        : []),
      ...(state.viewAttendanceNamesCompanyPeopleComment
        ? [TechCrewPermission.VIEW_ATTENDANCE_NAMES_COMPANY_PEOPLE_COMMENT]
        : []),
      ...(state.viewBoqPositionsHours
        ? [TechCrewPermission.VIEW_BOQ_POSITIONS_HOURS]
        : []),
      ...(state.viewAttendancePeopleCount
        ? [TechCrewPermission.VIEW_ATTENDANCE_PEOPLE_COUNT]
        : []),
      ...(state.viewAttendanceTotalHours
        ? [TechCrewPermission.VIEW_ATTENDANCE_TOTAL_HOURS]
        : []),
      ...(state.exportDataDocumentation
        ? [TechCrewPermission.EXPORT_DATA_DOCUMENTATION]
        : []),
      ...(state.viewDocuments ? [TechCrewPermission.VIEW_DOCUMENTS] : []),
      ...(needsViewAnalysis || state.viewAnalysis
        ? [TechCrewPermission.VIEW_ANALYSIS]
        : []),
      ...(state.editAnalysis ? [TechCrewPermission.EDIT_ANALYSIS] : []),
    ];
    onSubmit(newPermissions);
  }

  const currentProject = useProject();

  const isFreeProject = currentProject.license.type === LicenseType.FREE;
  const [isBoqAlertOpen, setIsBoqAlertOpen] = useState(false);

  return (
    <Modal
      className="flex min-w-[700px] flex-col space-y-6"
      isOpen={isOpen}
      onRequestClose={onClose}
    >
      <Modal.Header onClose={onClose}>
        {t('project:techCrewEdit.membersView.editPermissions.title')}
      </Modal.Header>
      <form onSubmit={handleSubmit(onSave)}>
        <div className="flex flex-col justify-between space-y-4 bg-gray-100 p-6">
          {isAdmin && (
            <InlineMessage variant="warning">
              {t('project:techCrewEdit.membersView.editPermissions.adminInfo')}
            </InlineMessage>
          )}
          <ul className="flex flex-col space-y-[1px]">
            <PermissionListHeader
              label={t(
                'project:techCrewEdit.membersView.editPermissions.planning',
              )}
              checked={true}
              disabled={true}
            />
            <PermissionsListItem
              disabled={true}
              checkedWhenDisabled={true}
              label={t(
                isFreeProject
                  ? 'project:techCrewEdit.membersView.editPermissions.viewPlanningFree'
                  : 'project:techCrewEdit.membersView.editPermissions.viewPlanning',
              )}
              icon={faEye}
            />
          </ul>
          <InlineMessage>
            {t('project:techCrewEdit.membersView.editPermissions.planningInfo')}
          </InlineMessage>
          <div className="flex flex-col space-y-4">
            <ul className="flex flex-col space-y-[1px]">
              <PermissionListHeader
                label={t(
                  'project:techCrewEdit.membersView.editPermissions.documentation',
                )}
                checked={isDocumentationChecked}
                disabled={isAdmin}
                indeterminate={isDocumentationIndeterminate}
                onClick={(flag) => {
                  setValue('viewDocumentation', flag, {
                    shouldDirty: true,
                  });
                  setValue('editDocumentation', flag, {
                    shouldDirty: true,
                  });
                  setValue('controlDocumentation', flag, {
                    shouldDirty: true,
                  });
                  setValue('viewAttendanceNamesWorktimeHours', flag, {
                    shouldDirty: true,
                  });
                  setValue('viewAttendanceNamesCompanyPeopleComment', flag, {
                    shouldDirty: true,
                  });
                  setValue('viewBoqPositionsHours', flag, {
                    shouldDirty: true,
                  });
                  setValue('viewAttendancePeopleCount', flag, {
                    shouldDirty: true,
                  });
                  setValue('viewAttendanceTotalHours', flag, {
                    shouldDirty: true,
                  });
                  setValue('exportDataDocumentation', flag, {
                    shouldDirty: true,
                  });
                  setValue('viewDocuments', flag, {
                    shouldDirty: true,
                  });
                }}
              />
              {needsViewDocumentation ? (
                <PermissionsListItem
                  disabled={true}
                  checkedWhenDisabled={true}
                  label={t(
                    'project:techCrewEdit.membersView.editPermissions.viewDocumentation',
                  )}
                  icon={faEye}
                />
              ) : (
                <PermissionsListItem
                  disabled={isAdmin}
                  checkedWhenDisabled={watch('viewDocumentation')}
                  label={t(
                    'project:techCrewEdit.membersView.editPermissions.viewDocumentation',
                  )}
                  icon={faEye}
                  register={register('viewDocumentation')}
                />
              )}
              <PermissionsListItem
                disabled={isAdmin}
                checkedWhenDisabled={watch('editDocumentation')}
                label={t(
                  'project:techCrewEdit.membersView.editPermissions.editDocumentation',
                )}
                icon={faPlusSquare}
                register={register('editDocumentation')}
              />
              {!isFreeProject && (
                <PermissionsListItem
                  disabled={isAdmin}
                  checkedWhenDisabled={watch('controlDocumentation')}
                  label={t(
                    'project:techCrewEdit.membersView.editPermissions.controlDocumentation',
                  )}
                  icon={faCheckSquare}
                  infoMessage={t(
                    'project:techCrewEdit.membersView.editPermissions.controlDocumentationInfo',
                  )}
                  register={register('controlDocumentation', {
                    onChange: (event) => {
                      if (!event.target.checked) {
                        setValue('viewAttendanceNamesWorktimeHours', false, {
                          shouldDirty: true,
                        });
                        setValue(
                          'viewAttendanceNamesCompanyPeopleComment',
                          false,
                          {
                            shouldDirty: true,
                          },
                        );
                        setValue('viewBoqPositionsHours', false, {
                          shouldDirty: true,
                        });
                        setValue('viewAttendancePeopleCount', false, {
                          shouldDirty: true,
                        });
                        setValue('viewAttendanceTotalHours', false, {
                          shouldDirty: true,
                        });
                      }
                    },
                  })}
                />
              )}
              {!isFreeProject && (
                <>
                  <PermissionListHeader
                    label={t(
                      'project:techCrewEdit.membersView.editPermissions.controllerVisibility',
                    )}
                    infoMessage={t(
                      'project:techCrewEdit.membersView.editPermissions.controllerVisibilityInfo',
                    )}
                    checked={
                      watch('viewAttendanceNamesWorktimeHours') &&
                      watch('viewAttendanceNamesCompanyPeopleComment') &&
                      watch('viewBoqPositionsHours') &&
                      watch('viewAttendancePeopleCount') &&
                      watch('viewAttendanceTotalHours')
                    }
                    disabled={isAdmin || !watch('controlDocumentation')}
                    indeterminate={
                      watch('viewAttendanceNamesWorktimeHours') ||
                      watch('viewAttendanceNamesCompanyPeopleComment') ||
                      watch('viewBoqPositionsHours') ||
                      watch('viewAttendancePeopleCount') ||
                      watch('viewAttendanceTotalHours')
                    }
                    onClick={(flag) => {
                      setValue('viewAttendanceNamesWorktimeHours', flag, {
                        shouldDirty: true,
                      });
                      setValue(
                        'viewAttendanceNamesCompanyPeopleComment',
                        flag,
                        {
                          shouldDirty: true,
                        },
                      );
                      setValue('viewBoqPositionsHours', flag, {
                        shouldDirty: true,
                      });
                      setValue('viewAttendancePeopleCount', flag, {
                        shouldDirty: true,
                      });
                      setValue('viewAttendanceTotalHours', flag, {
                        shouldDirty: true,
                      });
                    }}
                  />
                  <PermissionsListItem
                    disabled={isAdmin || !watch('controlDocumentation')}
                    checkedWhenDisabled={watch(
                      'viewAttendanceNamesWorktimeHours',
                    )}
                    label={t(
                      'project:techCrewEdit.membersView.editPermissions.viewAttendanceNamesWorktimeHours',
                    )}
                    icon={faEye}
                    register={register('viewAttendanceNamesWorktimeHours', {
                      onChange: (event) => {
                        if (
                          !event.target.checked &&
                          !watch('viewAttendanceNamesCompanyPeopleComment')
                        ) {
                          setValue('viewBoqPositionsHours', false, {
                            shouldDirty: true,
                          });
                        }
                      },
                    })}
                  />
                  <PermissionsListItem
                    disabled={isAdmin || !watch('controlDocumentation')}
                    checkedWhenDisabled={watch(
                      'viewAttendanceNamesCompanyPeopleComment',
                    )}
                    label={t(
                      'project:techCrewEdit.membersView.editPermissions.viewAttendanceNamesCompanyPeopleComment',
                    )}
                    icon={faEye}
                    register={register(
                      'viewAttendanceNamesCompanyPeopleComment',
                      {
                        onChange: (event) => {
                          if (
                            !event.target.checked &&
                            !watch('viewAttendanceNamesWorktimeHours')
                          ) {
                            setValue('viewBoqPositionsHours', false, {
                              shouldDirty: true,
                            });
                          }
                        },
                      },
                    )}
                  />
                  <PermissionsListItem
                    disabled={isAdmin || !watch('controlDocumentation')}
                    checkedWhenDisabled={watch('viewBoqPositionsHours')}
                    label={t(
                      'project:techCrewEdit.membersView.editPermissions.viewBoqPositionsHours',
                    )}
                    icon={faEye}
                    register={register('viewBoqPositionsHours', {
                      onChange: (event) => {
                        if (event.target.checked && !canEnableBoq) {
                          setValue('viewBoqPositionsHours', false, {
                            shouldDirty: true,
                          });
                          setIsBoqAlertOpen(true);
                        }
                      },
                    })}
                  />
                  <PermissionsListItem
                    disabled={isAdmin || !watch('controlDocumentation')}
                    checkedWhenDisabled={watch('viewAttendancePeopleCount')}
                    label={t(
                      'project:techCrewEdit.membersView.editPermissions.viewAttendancePeopleCount',
                    )}
                    icon={faEye}
                    register={register('viewAttendancePeopleCount')}
                  />
                  <PermissionsListItem
                    disabled={isAdmin || !watch('controlDocumentation')}
                    checkedWhenDisabled={watch('viewAttendanceTotalHours')}
                    label={t(
                      'project:techCrewEdit.membersView.editPermissions.viewAttendanceTotalHours',
                    )}
                    icon={faEye}
                    register={register('viewAttendanceTotalHours')}
                  />
                </>
              )}
            </ul>
            <ul className="flex flex-col space-y-[1px]">
              <PermissionsListItem
                disabled={isAdmin}
                checkedWhenDisabled={watch('exportDataDocumentation')}
                label={t(
                  'project:techCrewEdit.membersView.editPermissions.exportDataDocumentation',
                )}
                icon={faFileExport}
                register={register('exportDataDocumentation', {
                  onChange: (event) => {
                    if (event.target.checked && watch('controlDocumentation')) {
                      setValue('viewAttendanceNamesWorktimeHours', true, {
                        shouldDirty: true,
                      });
                      setValue('viewBoqPositionsHours', true, {
                        shouldDirty: true,
                      });
                    }
                  },
                })}
              />
              {!isFreeProject && (
                <PermissionsListItem
                  disabled={isAdmin}
                  checkedWhenDisabled={watch('viewDocuments')}
                  label={t(
                    'project:techCrewEdit.membersView.editPermissions.viewDocuments',
                  )}
                  icon={faEye}
                  register={register('viewDocuments')}
                  infoMessage={t(
                    'project:techCrewEdit.membersView.editPermissions.documentsInfo',
                  )}
                />
              )}
            </ul>
          </div>
          {!isFreeProject && (
            <ul className="flex flex-col space-y-[1px]">
              <PermissionListHeader
                label={t(
                  'project:techCrewEdit.membersView.editPermissions.analysis',
                )}
                checked={isAnalysisChecked}
                disabled={isAdmin}
                indeterminate={isAnalysisIndeterminate}
                onClick={(flag) => {
                  setValue('viewAnalysis', flag, {
                    shouldDirty: true,
                  });
                  setValue('editAnalysis', flag, { shouldDirty: true });
                }}
              />
              {needsViewAnalysis ? (
                <PermissionsListItem
                  disabled={true}
                  checkedWhenDisabled={true}
                  label={t(
                    'project:techCrewEdit.membersView.editPermissions.viewAnalysis',
                  )}
                  icon={faEye}
                />
              ) : (
                <PermissionsListItem
                  disabled={isAdmin}
                  checkedWhenDisabled={watch('viewAnalysis')}
                  label={t(
                    'project:techCrewEdit.membersView.editPermissions.viewAnalysis',
                  )}
                  icon={faEye}
                  register={register('viewAnalysis')}
                />
              )}
              <PermissionsListItem
                disabled={isAdmin}
                checkedWhenDisabled={watch('editAnalysis')}
                label={t(
                  'project:techCrewEdit.membersView.editPermissions.editAnalysis',
                )}
                icon={faCog}
                register={register('editAnalysis')}
              />
            </ul>
          )}
        </div>
        <div className="flex h-full items-center justify-end p-6">
          <Button className="mr-4" variant="tertiary" onClick={onClose}>
            {t('common:button.abort')}
          </Button>
          <Button type="submit" disabled={!isDirty}>
            {t('common:button.save')}
          </Button>
        </div>
      </form>
      <Modal
        isOpen={isBoqAlertOpen}
        onRequestClose={() => setIsBoqAlertOpen(false)}
      >
        <div className="flex w-[432px] flex-col p-4">
          <h3 className="mb-3 text-xl font-bold">
            {t(
              'project:techCrewEdit.membersView.editPermissions.viewBoqPositionsHours',
            )}
          </h3>
          <InlineMessage variant="warning" className="mb-4">
            {t(
              'project:techCrewEdit.membersView.editPermissions.viewBoqPositionsHoursAlert',
            )}
          </InlineMessage>
          <div className="flex justify-end">
            <Button onClick={() => setIsBoqAlertOpen(false)}>
              {t('common:button.close')}
            </Button>
          </div>
        </div>
      </Modal>
    </Modal>
  );
};

export const PermissionListHeader = ({
  label,
  checked,
  indeterminate,
  onClick,
  disabled,
  infoMessage,
}: {
  label: string;
  checked: boolean;
  indeterminate?: boolean;
  onClick?: (flag: boolean) => void;
  disabled?: boolean;
  infoMessage?: string;
}) => {
  return (
    <li className="flex justify-between bg-white p-4 shadow">
      <div className="flex items-center gap-3">
        <span className="font-bold">{label}</span>
        {infoMessage && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>{infoMessage}</span>
          </div>
        )}
      </div>
      <Checkbox
        onChange={() => onClick && onClick(!checked)}
        checked={checked}
        indeterminate={indeterminate}
        disabled={disabled}
      />
    </li>
  );
};

const PermissionsListItem = ({
  label,
  icon,
  disabled = false,
  register,
  checkedWhenDisabled,
  infoMessage,
}: {
  label: string;
  icon: IconDefinition;
  disabled?: boolean;
  register?: UseFormRegisterReturn;
  checkedWhenDisabled?: boolean;
  infoMessage?: string;
}) => {
  return (
    <li className="flex justify-between bg-white p-4 shadow">
      <div className="flex items-center gap-3">
        <div>
          <FontAwesomeIcon icon={icon} className="text-shuttleGray-500" />
          <span className="ml-3">{label}</span>
        </div>
        {infoMessage && (
          <div className="flex items-center gap-2 text-sm text-blue-600">
            <FontAwesomeIcon icon={faInfoCircle} />
            <span>{infoMessage}</span>
          </div>
        )}
      </div>
      {disabled ? (
        <Checkbox checked={checkedWhenDisabled} disabled={true} />
      ) : (
        <Checkbox {...register} />
      )}
    </li>
  );
};
